import {
  AddAvailableTableTagCoreAction,
  AddExtraTableToPartyCoreAction,
  AddTextMessageToPartyCoreAction,
  AddWalkInPartyCoreAction,
  ApplyEntitySetFromDbCoreAction,
  AssignBusinessServerToTableCoreAction,
  AssignPartyToTableCoreAction,
  CloseAllPartiesCoreAction,
  CommMessage,
  CutServerCoreAction,
  EntitySetFromDb,
  HasFloorplan,
  ID,
  MarkTextMessagesAsReadCoreAction,
  MoveTableCoreAction,
  NoFloorplan,
  PartyAndTable,
  PartyUpdate,
  RemoveAllTablesFromPartyCoreAction,
  RemoveAvailableTableTagCoreAction,
  RemovePartyFromTableCoreAction,
  RemoveServerCoreAction,
  RemoveTextMessageFromPartyCoreAction,
  RemoveWalkInPartyCoreAction,
  ResetAppStateCoreAction,
  ResetTablePositionsCoreAction,
  SetFloorplanCoreAction,
  SetPartyWithIdToPartyCoreAction,
  SetTextMessagesForPartyCoreAction,
  SetTimesForPartyWithIdCoreAction,
  SourcedMessage,
  TMClientAction,
  TMCoreAction,
  TMCoreEntityCreationCoreAction,
  TMCoreEntityDeletionCoreAction,
  TMCoreEntityModificationCoreAction,
  TMCoreEntityUpdateCoreAction,
  TableLayout,
  TimestampedActions,
  UpdatePartyCoreAction,
  UpdatePartyStateCoreAction,
  UpdateTablePartyStateCoreAction,
  UpdateTableStateCoreAction,
  WebsocketAction,
  WebsocketActionPayload,
} from './types';
import { BusinessTag, ServiceStateAppStrict } from '../app';
import {
  ElmPartyType,
  ElmPatron,
  ElmWalkInPartyDescription,
  PartyFromElm,
  PartyState,
  TableCombination,
  TableFromElm,
} from 'types/service';
import { ServiceParseError, UnknownActionError } from 'state/service/appTs/serviceErrors';
import { TableState } from 'constants/service/tableStateConstants';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { computePartyTypeFromId } from 'state/service/appTs/helpers';
import { dashboard } from 'types';
import { tocktix as dashboardProto } from '@tock/protos/dist/types/dashboard';
import { flatMap, keyBy, mapValues, sortBy, uniq } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';
import { tocktix as ssmProto } from '@tock/protos/dist/types/ssm';

type MessageSetExtension = keyof ssmProto.lib.IMessageSet;

export function valueOrFail<T>(v: T | null | undefined): T {
  if (v === null || v === undefined) {
    throw new ServiceParseError('missing value');
  }
  return v;
}

export function maybeValue<T>(v: T | null | undefined): T | undefined {
  return v || undefined;
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/TextMessage.elm#L48-L81
 */
export function decodeTextMessage(
  textMessage: dashboardProto.proto.dashboard.ITextMessage
): dashboard.TextMessage {
  return {
    id: textMessage.id || 0,
    businessTextNumber: textMessage.businessTextNumber || '',
    isRead: textMessage.isRead || false,
    label: textMessage.label || '',
    message: valueOrFail(textMessage.message),
    partyId: textMessage.partyId as number, // This is a lie, and is a result of types being wrong elsewhere.
    patronTextNumber: textMessage.patronTextNumber || '',
    senderName: textMessage.senderName || '',
    sentAtMs: valueOrFail(textMessage.sentAtMs),
    status: textMessage.status === 'RECEIVED' ? 'RECEIVED' : 'SENT',
    messageType: textMessage.messageType || 'RECEIVED_UNKNOWN',
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Party.elm#L496-L516
 */
export function decodeParty(party: dashboardProto.proto.dashboard.IParty): PartyFromElm {
  const id = valueOrFail(party.id);
  const partyType = party.partyType ? decodePartyType(party.partyType) : computePartyTypeFromId(id);
  const baseParty = {
    id,
    arrivalTime: maybeValue(party.arrivalTime),
    experience: valueOrFail(party.experience),
    experienceId: maybeValue(party.experienceId),
    expectedPartySize: party.expectedPartySize || 0,
    expectedReservationTime: party.expectedReservationTime || null,
    ticketDateTime: maybeValue(party.expectedReservationTime), // Different fields, but matches the Elm code https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Party.elm#L556
    leftTime: maybeValue(party.leftTime),
    numPeoplePresent: party.numPeoplePresent || 0,
    quotedTimeMinutes: maybeValue(party.quotedTimeMinutes),
    seatedTime: maybeValue(party.seatedTime),
    secondaryState: maybeValue(party.secondaryState),
    shortNotes: party.shortNotes || '',
    state: party.state ? decodePartyState(party.state) : 'EXPECTED',
    tag: party.tag || [],
    textMessage: (party.textMessage || []).map(decodeTextMessage),
    walkInCreatedTimeSec: maybeValue(party.walkInCreatedTimeSec),
    patronInfo: party.patronInfo ? decodePatronInfo(party.patronInfo) : {},
  };
  if (partyType.partyType === 'PurchaseParty') {
    return {
      ...baseParty,
      partyType: partyType,
    };
  } else {
    return {
      ...baseParty,
      partyType: partyType,
    };
  }
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Entities/Party.elm#L496-L514
 */
function decodePartyState(state: dashboardProto.proto.dashboard.PartyState): PartyState {
  switch (state) {
    case 'EXPECTED':
    case 'SEATED':
    case 'LEFT':
    case 'NO_SHOW':
    case 'CANCELED':
    case 'ARRIVED':
    case 'PARTIALLY_ARRIVED':
    case 'PARTIALLY_SEATED':
      return state;

    // deprecated values
    case 'PAID':
      return 'SEATED';
    case 'AWAITING_RESPONSE':
    case 'CONFIRMED_VIA_TEXT':
    case 'CONFIRMED_VIA_EMAIL':
      return 'EXPECTED';
    case 'FINISHED':
      return 'LEFT';
    default:
      return assertNever(state);
  }
}

function decodePatronInfo(
  patronInfo: dashboardProto.proto.dashboard.IServicePatronInfo
): ElmPatron {
  return {
    email: maybeValue(patronInfo.email),
    firstName: maybeValue(patronInfo.firstName),
    lastName: maybeValue(patronInfo.lastName),
    phone: maybeValue(patronInfo.phone),
    phoneCountryCode: maybeValue(patronInfo.phone),
    id: maybeValue(patronInfo.id),
  };
}

function decodePartyType(partyType: dashboardProto.proto.dashboard.IPartyType): ElmPartyType {
  switch (partyType.partyType) {
    case 'PurchaseParty':
      return {
        partyType: 'PurchaseParty',
        currentPurchaseId: maybeValue(partyType.currentPurchaseId),
        originalPurchaseId: valueOrFail(partyType.originalPurchaseId),
        visitId: maybeValue(partyType.visitId),
      };
    case 'WalkInParty':
      return {
        partyType: 'WalkInParty',
        visitId: maybeValue(partyType.visitId),
      };
    default:
      throw new ServiceParseError('Illegal party type');
  }
}

function decodeTable(table: dashboardProto.proto.dashboard.ITable): TableFromElm {
  return {
    businessServerId: table.businessServerId || null,
    // floorplanId: maybeValue(table.floorplanId),
    id: valueOrFail(table.id),
    isArchived: table.isArchived || false,
    location: table.location ? decodeTableLocation(table.location) : undefined,
    name: valueOrFail(table.name),
    room: table.room || '', // note: this is a departure from Elm which lets this be missing
    section: valueOrFail(table.section),
    size: valueOrFail(table.size),
    minPartySize: table.minPartySize || undefined,
    state: table.state ? decodeTableState(table.state) : 'Ready',
    tag: table.tag ? decodeTableTags(table.tag) : [],
  };
}

function decodeTableLocation(
  location: dashboardProto.proto.dashboard.ITableLocation
): TableLocation {
  return {
    defaultPosition: location.defaultPosition
      ? decodeDefaultPosition(location.defaultPosition)
      : undefined,
    rotation: valueOrFail(location.rotation),
    shape: valueOrFail(location.shape),
    visualSize: valueOrFail(location.visualSize),
    xPosition: valueOrFail(location.xPosition),
    yPosition: valueOrFail(location.yPosition),
  };
}

function decodeDefaultPosition(
  position: dashboardProto.proto.dashboard.ITablePosition
): TablePosition {
  return {
    xPosition: valueOrFail(position.xPosition),
    yPosition: valueOrFail(position.yPosition),
  };
}

function decodeTableState(stateString: string): TableState {
  switch (stateString) {
    case 'Ready':
    case 'Dirty':
    case 'Blocked':
      return stateString;
    default:
      throw new ServiceParseError(`Illegal table state ${stateString}`);
  }
}

function extractCoreActionsFromCommMessage(commMessage: CommMessage): TMCoreAction[] {
  switch (commMessage.type) {
    case 'Core':
      return [commMessage.action];
    case 'Batch':
      return flatMap(commMessage.actions, extractCoreActionsFromCommMessage);
    default:
      return [];
  }
}

function decodeEventToTimestampedActions(
  wrapper: dashboardProto.proto.dashboard.IEventWrapper
): TimestampedActions {
  const sourcedMessage = safeDecodeWebsocketEvent(wrapper);
  switch (sourcedMessage.type) {
    case 'SourcedMessage': {
      const {
        ids: { eventId },
        clientMeta: { timestampSecs: timestamp },
      } = sourcedMessage.source;

      const actions = extractCoreActionsFromCommMessage(sourcedMessage.commMessage);
      return {
        eventId: eventId || 0,
        timestamp,
        actions,
      };
    }
    case 'MalformedMessage': {
      throw new ServiceParseError(
        `Event ${sourcedMessage.serverId} was malformed: ${sourcedMessage.errorMessage}`
      );
    }
    case 'UnknownMessage': {
      throw new ServiceParseError(`Event ${sourcedMessage.serverId} was unknown`);
    }
    default: {
      // istanbul ignore next
      assertNever(sourcedMessage);
      throw new ServiceParseError('Bad comm message type');
    }
  }
}

export function decodeEventsToTimestampedActions(
  eventList: dashboardProto.proto.dashboard.IEventWrapper[]
): TimestampedActions[] {
  return eventList.map(decodeEventToTimestampedActions);
}

function safeDecodeWebsocketEvent(
  eventWrapper: dashboardProto.proto.dashboard.IEventWrapper
): SourcedMessage {
  try {
    return decodeEventWrapper(eventWrapper);
  } catch (e) {
    const eventId = eventWrapper.eventId;
    if (!eventId) {
      throw new ServiceParseError(
        `Websocket item ${JSON.stringify(eventWrapper)} was missing an event ID`
      );
    }
    if (e instanceof ServiceParseError) {
      if (!eventId) {
        throw new ServiceParseError(
          `Websocket item ${JSON.stringify(eventWrapper)} was missing an event ID`
        );
      }
      return {
        type: 'MalformedMessage',
        serverId: eventId,
        errorMessage: `Error parsing event ${JSON.stringify(eventWrapper)}: ${e.message}`,
      };
    }
    if (e instanceof UnknownActionError) {
      return {
        type: 'UnknownMessage',
        serverId: eventId,
      };
    }
    throw e;
  }
}

export function decodeEventWrapper(
  eventWrapper: dashboardProto.proto.dashboard.IEventWrapper
): SourcedMessage {
  const { eventId, clientInfo, timestamp: timestampSecs } = eventWrapper;

  const source = 'server'; // Pretty sure this never worked.
  if (!eventWrapper.event || !eventId || !timestampSecs) {
    throw new ServiceParseError('Error parsing action');
  }
  const action = decodeWebsocketCommMessage(eventWrapper.event);
  const sender = oc(clientInfo).displayName() || 'Someone';

  return {
    type: 'SourcedMessage',
    source: { ids: { clientId: source, eventId }, clientMeta: { sender, timestampSecs } },
    commMessage: action,
  };
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Client/Serialization/Proto.elm#L187-L199
 */
export function decodeWebsocketMessages(proto: ssmProto.lib.IMessageSet): SourcedMessage[] {
  const eventWrapper = proto['.tocktix.proto.dashboard.EventWrapper.messageSetExtension'];
  if (eventWrapper) {
    return [decodeEventWrapper(eventWrapper)];
  }
  const batchEventResponse =
    proto['.tocktix.proto.dashboard.BatchEventResponse.messageSetExtension'];
  if (batchEventResponse) {
    return decodeWebsocketEventBatch(batchEventResponse);
  }

  return []; // TODO
}

function decodeWebsocketEventBatch(
  proto: ssmProto.proto.dashboard.IBatchEventResponse
): SourcedMessage[] {
  if (proto.event) {
    return proto.event.map(safeDecodeWebsocketEvent);
  }
  throw new ServiceParseError('Batch event response missing events');
}

export function decodeWebsocketActionPayload(payload: WebsocketActionPayload): WebsocketAction {
  if (payload.Ready) {
    return { type: 'Ready' };
  } else if (payload.Message) {
    return { type: 'Message', messages: decodeWebsocketMessages(payload.Message.actions) };
  } else if (payload.ServerConnectionState) {
    return {
      type: 'ServerConnectionState',
      serverConnectionState: payload.ServerConnectionState.serverConnectionState,
    };
  }
  throw new ServiceParseError('Unknown payload in decodeWebsocketActionType');
}

export function websocketActionToClient(action: WebsocketAction): TMClientAction {
  switch (action.type) {
    case 'Ready':
      return { type: 'WebsocketReady' };
    case 'Message':
      return { type: 'ApplyMessages', messages: action.messages };
    case 'ServerConnectionState':
      return {
        type: 'SetServerConnectionState',
        serverConnectionState: action.serverConnectionState,
      };
  }
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Client/Serialization/Proto.elm#L675-L699
 */
function decodeWebsocketCommMessage(messageSet: ssmProto.lib.IMessageSet): CommMessage {
  for (const mse in messageSet) {
    const messageSetExtension = mse as MessageSetExtension;
    switch (messageSetExtension) {
      case '.tocktix.proto.dashboard.BatchAction.messageSetExtension': {
        return {
          type: 'Batch',
          actions: valueOrFail(valueOrFail(messageSet[messageSetExtension]).action).map(
            decodeWebsocketCommMessage
          ),
        };
      }
      case '.tocktix.proto.dashboard.ChatMessageAction.messageSetExtension': {
        return {
          type: 'Chat',
          text: valueOrFail(valueOrFail(messageSet[messageSetExtension]).text),
        };
      }
      case '.tocktix.proto.dashboard.SubscribeAction.messageSetExtension': {
        return { type: 'ClientConnect' };
      }
      default:
        return {
          type: 'Core',
          action: decodeWebsocketAction(messageSet),
        };
    }
  }
  throw new UnknownActionError();
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/Client/Serialization/Proto.elm#L240-L521
 */
export function decodeWebsocketAction(messageSet: ssmProto.lib.IMessageSet): TMCoreAction {
  for (const mse in messageSet) {
    const messageSetExtension = mse as MessageSetExtension;
    switch (messageSetExtension) {
      case '.tocktix.proto.dashboard.AssignBusinessServerToTable.messageSetExtension':
        return decodeAssignBusinessServerToTable(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.AddExtraTableToParty.messageSetExtension':
        return decodeAddExtraTableToParty(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.AddWalkInPartyAction.messageSetExtension':
        return decodeAddWalkInPartyAction(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.AssignPartyToTableAction.messageSetExtension':
        return decodeAssignPartyToTableAction(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.RemoveServer.messageSetExtension':
        return decodeRemoveServer(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.CutServer.messageSetExtension':
        return decodeCutServer(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.UpdatePartyStateAction.messageSetExtension':
        return decodeUpdatePartyStateAction(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.AddTextMessageToParty.messageSetExtension':
        return decodeAddTextMessageToParty(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.RemoveWalkInPartyAction.messageSetExtension':
        return decodeRemoveWalkInPartyAction(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.CloseAllParties.messageSetExtension':
        return decodeCloseAllParties(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.EntityModificationAction.messageSetExtension':
        return decodeEntityModificationAction(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.SetFloorplan.messageSetExtension':
        return decodeSetFloorplan(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.AddAvailableTableTag.messageSetExtension':
        return decodeAddAvailableTableTag(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.MarkTextMessagesAsRead.messageSetExtension':
        return decodeMarkTextMessagesAsRead(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.MoveTable.messageSetExtension':
        return decodeMoveTable(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.ServiceData.messageSetExtension':
        return decodeServiceDataProto(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.EntitySetFromDb.messageSetExtension':
        return decodeEntitySetFromDb(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.RemoveAvailableTableTag.messageSetExtension':
        return decodeRemoveAvailableTableTag(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.RemoveAllTablesFromParty.messageSetExtension':
        return decodeRemoveAllTablesFromParty(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.RemovePartyFromTableAction.messageSetExtension':
        return decodeRemovePartyFromTableAction(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.RemoveTextMessageFromParty.messageSetExtension':
        return decodeRemoveTextMessageFromParty(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.ResetTablePositions.messageSetExtension':
        return decodeResetTablePositions(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.SetPartyWithIdToParty.messageSetExtension':
        return decodeSetPartyWithIdToParty(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.SetTextMessagesForParty.messageSetExtension':
        return decodeSetTextMessagesForParty(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.SetTimesForPartyWithId.messageSetExtension':
        return decodeSetTimesForPartyWithId(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.UpdateTableState.messageSetExtension':
        return decodeUpdateTableState(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.UpdateTablePartyStateAction.messageSetExtension':
        return decodeUpdateTablePartyStateAction(valueOrFail(messageSet[messageSetExtension]));
      case '.tocktix.proto.dashboard.UpdatePartyAction.messageSetExtension':
        return decodeUpdatePartyAction(valueOrFail(messageSet[messageSetExtension]));
      // istanbul ignore next
      default:
        throw new ServiceParseError(`${messageSetExtension} decoder not yet implemented`);
    }
  }
  throw new UnknownActionError();
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1928-L1941
 */
function decodePartyUpdateType(type: string): PartyUpdate['updateType'] {
  switch (type) {
    case 'AddTag':
    case 'RemoveTag':
    case 'SetShortNotes':
    case 'UpdatePartyFirstName':
    case 'UpdatePartyLastName':
    case 'UpdatePartyPhone':
    case 'UpdatePartyPhoneCountryCode':
    case 'UpdatePartyQuotedTimeMinutes':
    case 'UpdatePartyReservationTime':
    case 'UpdatePartySize':
      return type;
    default:
      throw new ServiceParseError(`Unsupported party update type: ${type}`);
  }
}

/**
 * Rewritten from https://github.com/tocktix/admin/blob/4ec9b3dd5931dd0fa0985d39bdb5d122a06ccfc4/service/src/elm/main/TMCore.elm#L1984-L1989
 */
function decodeUpdatePartyAction(
  message: dashboardProto.proto.dashboard.IUpdatePartyAction
): UpdatePartyCoreAction {
  const updateParty = valueOrFail(message.updateParty);
  return {
    type: 'UpdateParty',
    partyUpdate: {
      partyId: valueOrFail(message.partyId),
      updateType: decodePartyUpdateType(valueOrFail(updateParty.updateType)),
      newValue: valueOrFail(updateParty.newValue),
    },
  };
}

function decodeUpdateTablePartyStateAction(
  message: dashboardProto.proto.dashboard.IUpdateTablePartyStateAction
): UpdateTablePartyStateCoreAction {
  const partyStateUpdate = valueOrFail(message.partyStateUpdate);
  return {
    type: 'UpdateTablePartyState',
    tableId: valueOrFail(message.tableId),
    stateUpdateWithoutParty: {
      secondaryState: maybeValue(partyStateUpdate.secondaryState),
      timestampSecs: valueOrFail(partyStateUpdate.timestamp),
      state: decodePartyState(valueOrFail(partyStateUpdate.state)),
    },
  };
}

function decodeUpdateTableState(
  message: dashboardProto.proto.dashboard.IUpdateTableState
): UpdateTableStateCoreAction {
  return {
    type: 'UpdateTableState',
    tableAndState: {
      tableId: valueOrFail(message.tableId),
      newState: decodeTableState(valueOrFail(message.newState)),
    },
  };
}

function decodeSetTimesForPartyWithId(
  message: dashboardProto.proto.dashboard.ISetTimesForPartyWithId
): SetTimesForPartyWithIdCoreAction {
  return {
    type: 'SetTimesForPartyWithId',
    partyId: valueOrFail(message.partyId),
    leftTime: valueOrFail(message.leftTime),
    seatedTime: valueOrFail(message.seatedTime),
  };
}

function decodeSetTextMessagesForParty(
  message: dashboardProto.proto.dashboard.ISetTextMessagesForParty
): SetTextMessagesForPartyCoreAction {
  return {
    type: 'SetTextMessagesForParty',
    partyId: valueOrFail(message.partyId),
    textMessages: (message.textMessage || []).map(decodeTextMessage),
  };
}

function decodeSetPartyWithIdToParty(
  message: dashboardProto.proto.dashboard.ISetPartyWithIdToParty
): SetPartyWithIdToPartyCoreAction {
  return { type: 'SetPartyWithIdToParty', party: decodeParty(valueOrFail(message.party)) };
}

/**
 * Not really needed, but helps make every case of decodeWebsocketAction have a consistent look.
 */
function decodeResetTablePositions(
  _message: dashboardProto.proto.dashboard.IResetTablePositions
): ResetTablePositionsCoreAction {
  return { type: 'ResetTablePositions' };
}

function decodeRemoveTextMessageFromParty(
  message: dashboardProto.proto.dashboard.IRemoveTextMessageFromParty
): RemoveTextMessageFromPartyCoreAction {
  return {
    type: 'RemoveTextMessageFromParty',
    partyId: valueOrFail(message.partyId),
    textMessage: decodeTextMessage(valueOrFail(message.textMessage)),
  };
}

function decodeRemovePartyFromTableAction(
  message: dashboardProto.proto.dashboard.IRemovePartyFromTableAction
): RemovePartyFromTableCoreAction {
  const { partyId, tableId } = valueOrFail(message.ids);
  return {
    type: 'RemovePartyFromTable',
    partyAndTable: {
      partyId: valueOrFail(partyId),
      tableId: valueOrFail(tableId),
    },
  };
}

function decodeRemoveAllTablesFromParty(
  message: dashboardProto.proto.dashboard.IRemoveAllTablesFromParty
): RemoveAllTablesFromPartyCoreAction {
  return { type: 'RemoveAllTablesFromParty', partyId: valueOrFail(message.partyId) };
}

function decodeRemoveAvailableTableTag(
  message: dashboardProto.proto.dashboard.IRemoveAvailableTableTag
): RemoveAvailableTableTagCoreAction {
  const tag = decodeTableTag(valueOrFail(message.tag));
  return { type: 'RemoveAvailableTableTag', key: tag.key, val: tag.val };
}

function decodeEntitySetFromDbProto(
  message: dashboardProto.proto.dashboard.IEntitySetFromDb
): EntitySetFromDb {
  let tableLayout: TableLayout;
  if (message.floorplan) {
    tableLayout = decodeFloorplan(message.floorplan);
  } else if (message.noFloorplan) {
    tableLayout = decodeNoFloorplan(message.noFloorplan);
  } else {
    throw new ServiceParseError('EntitySetFromDb must have floorplan or nofloorplan');
  }

  return {
    parties: keyBy(valueOrFail(message.party).map(decodeParty), 'id'),
    partyIdToTableId: {},
    patronIdToPartyIds: valueOrFail(message.patronMapping).reduce(
      (mapping: DictionaryStrict<ID[]>, vals) => {
        if (vals.patronId && vals.partyId) {
          mapping[vals.patronId] = vals.partyId;
        }
        return mapping;
      },
      {}
    ),
    tableLayout,
  };
}

function decodeNoFloorplan(message: dashboardProto.proto.dashboard.INoFloorplan): NoFloorplan {
  return { type: 'NoFloorplan', tables: keyBy((message.table || []).map(decodeTable), 'id') };
}

function decodeEntitySetFromDb(
  message: dashboardProto.proto.dashboard.IEntitySetFromDb
): ApplyEntitySetFromDbCoreAction {
  return { type: 'ApplyEntitySetFromDb', entitySetFromDb: decodeEntitySetFromDbProto(message) };
}

function decodeServiceDataProto(
  message: dashboardProto.proto.dashboard.IServiceData
): ResetAppStateCoreAction {
  return { type: 'ResetAppState', newAppState: decodeServiceData(message) };
}

function decodeMoveTable(message: dashboardProto.proto.dashboard.IMoveTable): MoveTableCoreAction {
  const { xPosition, yPosition } = valueOrFail(message.newPosition);
  return {
    type: 'MoveTable',
    tableAndPosition: {
      tableId: valueOrFail(message.tableId),
      newPosition: { xPosition: valueOrFail(xPosition), yPosition: valueOrFail(yPosition) },
    },
  };
}

function decodeMarkTextMessagesAsRead(
  message: dashboardProto.proto.dashboard.IMarkTextMessagesAsRead
): MarkTextMessagesAsReadCoreAction {
  return { type: 'MarkTextMessagesAsRead', partyId: valueOrFail(message.partyId) };
}

function decodeAddAvailableTableTag(
  message: dashboardProto.proto.dashboard.IAddAvailableTableTag
): AddAvailableTableTagCoreAction {
  const { tag } = message;
  if (!tag) {
    throw new ServiceParseError('AddAvailableTableTag action has no tag');
  }
  return {
    type: 'AddAvailableTableTag',
    key: decodeTableTagKey(tag.key),
    val: valueOrFail(tag.val),
  };
}

function decodeTableTagKey(key?: string | null): AddAvailableTableTagCoreAction['key'] {
  if (key === 'SERVER') {
    return 'SERVER';
  }
  throw new ServiceParseError('Table tag key must be SERVER');
}

function decodeEntityModificationAction(
  message: dashboardProto.proto.dashboard.IEntityModificationAction
): TMCoreEntityModificationCoreAction {
  const { creationEvent, updateEvent, deletionEvent } = message;
  if (creationEvent) {
    return decodeEntityCreationEvent(creationEvent);
  } else if (updateEvent) {
    return decodeEntityUpdateEvent(updateEvent);
  } else if (deletionEvent) {
    return decodeEntityDeletionEvent(deletionEvent);
  }
  throw new ServiceParseError('Cannot parse entity modification action');
}

function decodeEntityDeletionEvent(
  message: dashboardProto.proto.dashboard.IEntityDeletionEvent
): TMCoreEntityDeletionCoreAction {
  const { partyId, tableId } = message;
  if (partyId) {
    return { type: 'RemoveParty', partyId };
  } else if (tableId) {
    return { type: 'RemoveTable', tableId };
  }
  throw new ServiceParseError('Cannot decode entity deletion event');
}

function decodeEntityUpdateEvent(
  message: dashboardProto.proto.dashboard.IEntityUpdateEvent
): TMCoreEntityUpdateCoreAction {
  if (message.updatedEntity) {
    const { partyAndTableAssignments, table, party } = message.updatedEntity;
    if (partyAndTableAssignments) {
      return {
        type: 'ReplaceParty',
        party: decodeParty(partyAndTableAssignments.party),
      };
    } else if (table) {
      return { type: 'UpdateTable', table: decodeTable(table) };
    } else if (party) {
      return { type: 'ReplaceParty', party: decodeParty(party) };
    }
  }
  throw new ServiceParseError('Cannot decode entity update event');
}

function decodeEntityCreationEvent(
  message: dashboardProto.proto.dashboard.IEntityCreationEvent
): TMCoreEntityCreationCoreAction {
  if (message.createdEntity) {
    const { textMessage, businessGroupTag, partyAndTableAssignments, table } =
      message.createdEntity;
    if (textMessage) {
      return !textMessage.partyId
        ? {
            type: 'AddTextMessage',
            textMessage: decodeTextMessage(textMessage),
          }
        : {
            type: 'AddTextMessageToParty',
            partyId: textMessage.partyId,
            textMessage: decodeTextMessage(textMessage),
          };
    } else if (businessGroupTag) {
      return {
        type: 'AddBusinessGroupTag',
        businessTag: decodeBusinessTag(businessGroupTag),
      };
    } else if (partyAndTableAssignments) {
      const { party, tableId } = partyAndTableAssignments;
      return {
        type: 'AddParty',
        party: decodeParty(party),
        tableIds: valueOrFail(tableId),
      };
    } else if (table) {
      return {
        type: 'AddTable',
        table: decodeTable(table),
      };
    }
  }
  throw new ServiceParseError('Cannot decode entity creation event');
}

function decodeTableTags(tags: dashboardProto.proto.dashboard.ITableTag[]): TableTag[] {
  return tags.map(decodeTableTag);
}

function decodeTableTag(tag: dashboardProto.proto.dashboard.ITableTag): TableTag {
  return {
    key: valueOrFail(tag.key),
    val: valueOrFail(tag.val),
  };
}

function decodeSetFloorplan(
  message: dashboardProto.proto.dashboard.ISetFloorplan
): SetFloorplanCoreAction {
  const { floorplan, newAssignment } = message;
  return {
    type: 'SetFloorplan',
    floorplan: decodeFloorplan(valueOrFail(floorplan)).floorplan,
    tableAssignments: (newAssignment || []).map(decodeTableAssignment),
  };
}

function decodeFloorplan(message: dashboardProto.proto.dashboard.IFloorplan): HasFloorplan {
  const { id, archivedTable, activeTable, tableCombination } = message;
  const tablesArray = [...(archivedTable || []), ...(activeTable || [])].map(decodeTable);
  return {
    type: 'HasFloorplan',
    floorplan: {
      id: valueOrFail(id),
      tables: keyBy(tablesArray, 'id'),
      tableCombinations: (tableCombination || []).map(decodeTableCombination),
    },
  };
}

function decodeTableCombination(
  message: dashboardProto.proto.dashboard.ITableCombination
): TableCombination {
  const { id, businessId, tableId, minPurchaseSize, maxPurchaseSize, isArchived } = message;
  return {
    id: valueOrFail(id),
    businessId: valueOrFail(businessId),
    tableId: valueOrFail(tableId),
    minPurchaseSize: valueOrFail(minPurchaseSize),
    maxPurchaseSize: valueOrFail(maxPurchaseSize),
    isArchived: valueOrFail(isArchived),
  };
}

function decodeTableAssignment(
  message: dashboardProto.proto.dashboard.SetFloorplan.ITableAssignment
): PartyAndTable {
  const { partyId, tableId } = message;
  return {
    partyId: valueOrFail(partyId),
    tableId: valueOrFail(tableId),
  };
}

function decodeCloseAllParties(
  message: dashboardProto.proto.dashboard.ICloseAllParties
): CloseAllPartiesCoreAction {
  return {
    type: 'CloseAllParties',
    timestampSecs: valueOrFail(message.timestampSecs),
  };
}

function decodeRemoveWalkInPartyAction(
  message: dashboardProto.proto.dashboard.IRemoveWalkInPartyAction
): RemoveWalkInPartyCoreAction {
  return {
    type: 'RemoveWalkInParty',
    partyId: valueOrFail(message.partyId),
  };
}

function decodeAddTextMessageToParty(
  message: dashboardProto.proto.dashboard.IAddTextMessageToParty
): AddTextMessageToPartyCoreAction {
  const { partyId, textMessage } = message;
  if (!partyId || !textMessage) {
    throw new ServiceParseError('Error parsing AddTextMessageToParty');
  }
  return {
    type: 'AddTextMessageToParty',
    partyId: valueOrFail(partyId),
    textMessage: decodeTextMessage(textMessage),
  };
}

function decodeUpdatePartyStateAction(
  message: dashboardProto.proto.dashboard.IUpdatePartyStateAction
): UpdatePartyStateCoreAction {
  const { partyId, state, secondaryState, timestamp } = valueOrFail(message.partyStateUpdate);
  return {
    type: 'UpdatePartyState',
    partyAndState: {
      partyId: valueOrFail(partyId),
      state: decodePartyState(valueOrFail(state)),
      secondaryState: maybeValue(secondaryState),
      timestamp: valueOrFail(timestamp),
    },
  };
}

function decodeAddExtraTableToParty(
  message: dashboardProto.proto.dashboard.IAddExtraTableToParty
): AddExtraTableToPartyCoreAction {
  if (!message.ids) {
    throw new ServiceParseError('Error parsing ids');
  }
  const { partyId, tableId } = message.ids;
  if (!partyId || !tableId) {
    throw new ServiceParseError('Party or table undefined');
  }
  return {
    type: 'AddExtraTableToParty',
    partyAndTable: { partyId, tableId },
  };
}

function decodeAddWalkInPartyAction(
  message: dashboardProto.proto.dashboard.IAddWalkInPartyAction
): AddWalkInPartyCoreAction {
  const description = message.description;
  if (!description) {
    throw new ServiceParseError('AddWalkInPartyAction had no walk-in party');
  }
  return {
    type: 'AddWalkInParty',
    partyDescription: decodeWalkInPartyDescription(description),
  };
}

function decodeAssignBusinessServerToTable(
  message: dashboardProto.proto.dashboard.IAssignBusinessServerToTable
): AssignBusinessServerToTableCoreAction {
  const { businessServerId, tableId } = message;
  if (!businessServerId || !tableId) {
    throw new ServiceParseError('AssignBusinessServerToTable must have server and table');
  }
  return {
    type: 'AssignBusinessServerToTable',
    businessServerAndTables: { businessServerId, tableId },
  };
}

function decodeAssignPartyToTableAction(
  message: dashboardProto.proto.dashboard.AssignPartyToTableAction
): AssignPartyToTableCoreAction {
  const ids = message.ids;
  if (!ids || !ids.tableId || !ids.partyId) {
    throw new ServiceParseError('AssignPartyToTableAction had no table and party IDs');
  }
  return {
    type: 'AssignPartyToTable',
    partyAndTable: { tableId: ids.tableId, partyId: ids.partyId },
  };
}

function decodeRemoveServer(
  message: dashboardProto.proto.dashboard.IRemoveServer
): RemoveServerCoreAction {
  const { tableId } = message;
  if (!tableId) {
    throw new ServiceParseError('RemoveServer had no tableId');
  }
  return {
    type: 'RemoveServer',
    tableIds: tableId,
  };
}

function decodeCutServer(message: dashboardProto.proto.dashboard.ICutServer): CutServerCoreAction {
  const { businessServerId } = message;
  if (!businessServerId) {
    throw new ServiceParseError('CutServer had no businessServerId');
  }
  return {
    type: 'CutServer',
    businessServerId,
  };
}

function decodeWalkInPartyDescription(
  description: dashboardProto.proto.dashboard.IWalkInPartyDescription
): ElmWalkInPartyDescription {
  return {
    id: valueOrFail(description.id),
    arrivalTime: maybeValue(description.arrivalTime),
    email: maybeValue(description.email),
    expectedReservationTime: maybeValue(description.expectedReservationTime),
    firstName: maybeValue(description.firstName),
    lastName: maybeValue(description.lastName),
    leftTime: maybeValue(description.leftTime),
    numPeoplePresent: valueOrFail(description.numPeoplePresent),
    phone: maybeValue(description.phone),
    quotedTimeMinutes: maybeValue(description.quotedTimeMinutes),
    seatedTime: maybeValue(description.seatedTime),
    state: description.state ? decodePartyState(description.state) : undefined,
    secondaryState: maybeValue(description.secondaryState),
    shortNotes: description.shortNotes || '',
    tag: description.tag || [],
    textMessage: description.textMessage || [],
    walkInCreatedTimeSec: maybeValue(description.walkInCreatedTimeSec),
    patronId: maybeValue(description.patronId),
  };
}

export function decodeServiceData(
  serviceData: dashboardProto.proto.dashboard.IServiceData
): ServiceStateAppStrict {
  const availableTableTags = decodeAvailableTableTags(serviceData.availableTableTags || []);
  const businessTag = decodeBusinessTags(serviceData.businessTag || []);
  const parties = decodeParties(serviceData.party || []);
  const tables = decodeTables(serviceData.table || []);
  const partyIdToTableId = decodePartyIdToTableId(serviceData.mapping || []);
  const patronIdToPartyIds = decodePatronIdToPartyIds(serviceData.patronMapping || []);
  const serverIdToPartyIds = decodeServerIdToPartyIds(serviceData.serverMapping || []);
  const handleDuplicateAddParty = serviceData.handleDuplicateAddParty || false;
  return {
    availableTableTags,
    businessTag,
    // floorplan,
    parties,
    tables,
    partyIdToTableId,
    patronIdToPartyIds,
    serverIdToPartyIds,
    handleDuplicateAddParty,
  };
}

function decodeAvailableTableTags(
  availableTableTags: dashboardProto.proto.dashboard.IAvailableTableTag[]
): Dictionary<string[]> {
  const returnValue: Dictionary<string[]> = {};
  for (const tag of availableTableTags) {
    if (!tag.tagName || !tag.tagValues) {
      throw new ServiceParseError('Bad available table tags value');
    }
    returnValue[tag.tagName] = uniq(sortBy([...returnValue[tag.tagName], ...tag.tagValues]));
  }
  return returnValue;
}

function decodeBusinessTags(tags: dashboardProto.proto.dashboard.ITag[]): Dictionary<BusinessTag> {
  return toMapById(tags.map(decodeBusinessTag));
}

function decodeBusinessTag(tag: dashboardProto.proto.dashboard.ITag): BusinessTag {
  if (!tag.id) {
    throw new ServiceParseError('Tag had no ID');
  }
  return {
    id: tag.id,
    text: tag.text || '',
    isPriority: tag.isPriority || false,
    type: tag.type || '',
    isArchived: tag.isArchived || false,
    ordering: tag.ordering || 0,
    icon: tag.icon || '',
  };
}

function decodeParties(
  parties: dashboardProto.proto.dashboard.IParty[]
): DictionaryStrict<PartyFromElm> {
  return toStrictNumericDictById(parties.map(decodeParty));
}

function decodeTables(
  tables: dashboardProto.proto.dashboard.ITable[]
): DictionaryStrict<TableFromElm> {
  return toStrictNumericDictById(tables.map(decodeTable));
}

function decodePartyIdToTableId(
  mapping: dashboardProto.proto.dashboard.IPartyToTableMapping[]
): DictionaryStrict<number[]> {
  return idListToMap(
    mapping,
    (t) => t.partyId,
    (t) => t.tableId
  );
}

function decodePatronIdToPartyIds(
  patronMapping: dashboardProto.proto.dashboard.IPatronToPartyMapping[]
): DictionaryStrict<number[]> {
  return keyAndValuesEntriesToMap(
    patronMapping,
    (t) => t.patronId,
    (t) => t.partyId
  );
}

function decodeServerIdToPartyIds(
  serverMapping: dashboardProto.proto.dashboard.IServerToPartyMapping[]
): DictionaryStrict<number[]> {
  return idListToMap(
    serverMapping,
    (t) => t.serverId,
    (t) => t.partyId
  );
}

/***************
 * DECODING UTILITY
 *
 * Functions that help with decoders.
 ***************/

function keyAndValuesEntriesToMap<T>(
  entries: T[],
  keyFn: (e: T) => number | null | undefined,
  valuesFn: (e: T) => number[] | null | undefined
): DictionaryStrict<number[]> {
  const returnValue: DictionaryStrict<number[]> = {};
  for (const entry of entries) {
    const key = keyFn(entry);
    const values = valuesFn(entry);
    if (!key || !values) {
      continue;
    }
    returnValue[key] = [...(returnValue[key] || []), ...values];
  }
  return returnValue;
}

function toMapById<T extends { id: ID }>(items: T[]): Dictionary<T> {
  const returnValue: Dictionary<T> = {};
  for (const item of items) {
    returnValue[item.id] = item;
  }
  return returnValue;
}

function toStrictNumericDictById<T extends { id: ID }>(items: T[]): DictionaryStrict<T> {
  const returnValue: DictionaryStrict<T> = {};
  for (const item of items) {
    returnValue[item.id] = item;
  }
  return returnValue;
}

function idListToMap<T>(
  entries: T[],
  keyFn: (e: T) => number | null | undefined,
  valFn: (e: T) => number | null | undefined
): DictionaryStrict<number[]> {
  const returnValue: DictionaryStrict<number[]> = {};
  for (const entry of entries) {
    const key = valueOrFail(keyFn(entry));
    const value = valueOrFail(valFn(entry));
    returnValue[key] = [...(returnValue[key] || []), value];
  }
  return mapValues(returnValue, (entry) => sortBy(entry));
}
